import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Plus,
  Building2,
  Hotel,
  Building,
  Home,
  ShoppingBag,
  Utensils,
  Warehouse,
  Plane,
  Ship,
  TreePine,
  Dumbbell,
  Hospital,
  GraduationCap,
  Store,
  Briefcase,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useCreatePropertyTypeMutation } from '@/hooks/queries/usePropertiesTemplates';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

interface AddPropertyTypeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface PropertyTypeData {
  nameEn: string;
  nameAr: string;
  icon: string;
  color: string;
}

// Available icons for property types
const availableIcons = [
  { name: 'Hotel', component: Hotel, label: 'Hotel' },
  { name: 'Building2', component: Building2, label: 'Office Building' },
  { name: 'Building', component: Building, label: 'Commercial Building' },
  { name: 'Home', component: Home, label: 'Residential' },
  { name: 'ShoppingBag', component: ShoppingBag, label: 'Shopping Mall' },
  { name: 'Store', component: Store, label: 'Retail Store' },
  { name: 'Utensils', component: Utensils, label: 'Restaurant' },
  { name: 'Warehouse', component: Warehouse, label: 'Warehouse' },
  { name: 'Plane', component: Plane, label: 'Airport' },
  { name: 'Ship', component: Ship, label: 'Marina/Port' },
  { name: 'TreePine', component: TreePine, label: 'Resort' },
  { name: 'Dumbbell', component: Dumbbell, label: 'Gym/Sports' },
  { name: 'Hospital', component: Hospital, label: 'Hospital/Clinic' },
  {
    name: 'GraduationCap',
    component: GraduationCap,
    label: 'School/University',
  },
  { name: 'Briefcase', component: Briefcase, label: 'Business Center' },
];

// Available colors - store name instead of hex
const availableColors = [
  { name: 'Blue', hex: '#3B82F6' },
  { name: 'Green', hex: '#10B981' },
  { name: 'Purple', hex: '#8B5CF6' },
  { name: 'Orange', hex: '#F59E0B' },
  { name: 'Red', hex: '#EF4444' },
  { name: 'Pink', hex: '#EC4899' },
  { name: 'Indigo', hex: '#6366F1' },
  { name: 'Teal', hex: '#14B8A6' },
  { name: 'Cyan', hex: '#06B6D4' },
  { name: 'Amber', hex: '#F59E0B' },
];

// Helper function to get hex from color name
const getColorHex = (colorName: string): string => {
  const color = availableColors.find(c => c.name === colorName);
  return color?.hex || '#3B82F6';
};

export function AddPropertyTypeDialog({
  open,
  onOpenChange,
}: AddPropertyTypeDialogProps) {
  const { toast } = useToast();
  const { t, i18n } = useTranslation();
  const isRTL = useMemo(() => i18n.language === 'ar', [i18n.language]);
  const [newPropertyType, setNewPropertyType] = useState<PropertyTypeData>({
    nameEn: '',
    nameAr: '',
    icon: '',
    color: '',
  });

  const createMutation = useCreatePropertyTypeMutation({
    onSuccessCallback: () => {
      onOpenChange(false);
      setNewPropertyType({ nameEn: '', nameAr: '', icon: '', color: '' });
    },
  });

  // Handle adding new property type
  const handleAddPropertyType = () => {
    if (
      newPropertyType.nameEn &&
      newPropertyType.nameAr &&
      newPropertyType.icon &&
      newPropertyType.color
    ) {
      createMutation.mutate({
        name_en: newPropertyType.nameEn,
        name_ar: newPropertyType.nameAr,
        icon_name: newPropertyType.icon,
        color: newPropertyType.color, // Send color name (e.g., "Blue")
      });
    } else {
      toast({
        title: t('templates.admin.addPropertyType.error', 'Validation Error'),
        description: t(
          'templates.admin.addPropertyType.fillAllFields',
          'Please fill all required fields'
        ),
        variant: 'destructive',
      });
    }
  };

  // Handle dialog close
  const handleClose = () => {
    onOpenChange(false);
    setNewPropertyType({ nameEn: '', nameAr: '', icon: '', color: '' });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn('max-w-3xl w-[90vw]', isRTL && 'rtl')}>
        <DialogHeader className={cn(isRTL && 'text-right')}>
          <DialogTitle
            className={cn(
              'flex items-center gap-2',
              isRTL ? 'flex-row-reverse' : 'flex-row'
            )}
          >
            <Plus className="w-5 h-5" />
            {t('templates.admin.addPropertyType.title')}
          </DialogTitle>
          <DialogDescription>
            {t('templates.admin.addPropertyType.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
          <div className="space-y-2">
            <Label
              htmlFor="property-type-name-en"
              className={cn(isRTL && 'text-right')}
            >
              {t('templates.admin.editTemplate.nameEnglish')}
            </Label>
            <Input
              id="property-type-name-en"
              placeholder="e.g., Shopping Mall, Office Building"
              value={newPropertyType.nameEn}
              onChange={e =>
                setNewPropertyType(prev => ({
                  ...prev,
                  nameEn: e.target.value,
                }))
              }
              className={cn(isRTL && 'text-right')}
              dir="ltr"
            />
          </div>

          <div className="space-y-2">
            <Label
              htmlFor="property-type-name-ar"
              className={cn(isRTL && 'text-right')}
            >
              {t('templates.admin.editTemplate.nameArabic')}
            </Label>
            <Input
              id="property-type-name-ar"
              placeholder="مثل: مجمع تجاري، مبنى مكاتب"
              value={newPropertyType.nameAr}
              onChange={e =>
                setNewPropertyType(prev => ({
                  ...prev,
                  nameAr: e.target.value,
                }))
              }
              dir="rtl"
              className="text-right"
            />
          </div>

          <div className="space-y-2">
            <Label
              htmlFor="property-type-icon"
              className={cn(isRTL && 'text-right')}
            >
              {t('templates.admin.addPropertyType.icon', 'Icon')}
            </Label>
            <Select
              value={newPropertyType.icon}
              onValueChange={value =>
                setNewPropertyType(prev => ({ ...prev, icon: value }))
              }
            >
              <SelectTrigger className={cn(isRTL && 'text-right')}>
                <SelectValue
                  placeholder={t(
                    'templates.admin.addPropertyType.selectIcon',
                    'Select an icon'
                  )}
                />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                <ScrollArea className="h-full">
                  {availableIcons.map(icon => {
                    const IconComponent = icon.component;
                    return (
                      <SelectItem key={icon.name} value={icon.name}>
                        <div
                          className={cn(
                            'flex items-center gap-2',
                            isRTL ? 'flex-row-reverse' : 'flex-row'
                          )}
                        >
                          <IconComponent className="w-4 h-4" />
                          <span>{icon.label}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </ScrollArea>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label
              htmlFor="property-type-color"
              className={cn(isRTL && 'text-right')}
            >
              {t('templates.admin.addPropertyType.color', 'Color')}
            </Label>
            <Select
              value={newPropertyType.color}
              onValueChange={value =>
                setNewPropertyType(prev => ({ ...prev, color: value }))
              }
            >
              <SelectTrigger className={cn(isRTL && 'text-right')}>
                <SelectValue
                  placeholder={t(
                    'templates.admin.addPropertyType.selectColor',
                    'Select a color'
                  )}
                />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                <ScrollArea className="h-full">
                  {availableColors.map(color => (
                    <SelectItem key={color.name} value={color.name}>
                      <div
                        className={cn(
                          'flex items-center gap-2',
                          isRTL ? 'flex-row-reverse' : 'flex-row'
                        )}
                      >
                        <div
                          className="w-4 h-4 rounded-full border"
                          style={{ backgroundColor: color.hex }}
                        />
                        <span>{color.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </ScrollArea>
              </SelectContent>
            </Select>
          </div>

          {/* Preview Section */}
          {(newPropertyType.icon || newPropertyType.color) && (
            <div className="col-span-1 md:col-span-2 space-y-2">
              <Label className={cn(isRTL && 'text-right')}>
                {t('templates.admin.addPropertyType.preview', 'Preview')}
              </Label>
              <div className="p-4 border rounded-lg bg-muted/30">
                <div
                  className={cn(
                    'flex items-center gap-3',
                    isRTL ? 'flex-row-reverse' : 'flex-row'
                  )}
                >
                  {newPropertyType.icon && (
                    <div
                      className="p-3 rounded-xl text-white shadow-lg"
                      style={{
                        backgroundColor: getColorHex(newPropertyType.color),
                      }}
                    >
                      {(() => {
                        const selectedIcon = availableIcons.find(
                          icon => icon.name === newPropertyType.icon
                        );
                        if (selectedIcon) {
                          const IconComponent = selectedIcon.component;
                          return <IconComponent className="w-5 h-5" />;
                        }
                        return null;
                      })()}
                    </div>
                  )}
                  <div className={cn(isRTL && 'text-right')}>
                    <div className="font-medium">
                      {newPropertyType.nameEn ||
                        t(
                          'templates.admin.addPropertyType.defaultName',
                          'Property Type Name'
                        )}
                    </div>
                    <div className="text-sm text-muted-foreground" dir="rtl">
                      {newPropertyType.nameAr || 'اسم نوع العقار'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className={cn(isRTL && 'flex-row-reverse')}>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={createMutation.isPending}
          >
            {t('templates.admin.editTemplate.cancel')}
          </Button>
          <Button
            onClick={handleAddPropertyType}
            disabled={createMutation.isPending}
          >
            {createMutation.isPending
              ? t('common.creating', 'Creating...')
              : t(
                  'templates.admin.addPropertyType.create',
                  'Create Property Type'
                )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
